import React, { useContext, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Overlay from "../../Overlay";
import { Form, Formik } from "formik";
import Button from "../../../components/common/Button";
import DatePicker from "../../../components/common/DatePicker";
import TextBox from "../../../components/common/TextBox";
import { startOfDay } from "date-fns";
import { ReactComponent as CalendarSvg } from "../../../assets/workExchange/icon-calendar.svg";
import { isValidNumber } from "../../../helpers/common.helper";

import useJournals from "../../../hooks/useJournals";
import useLandingPage from "../../../hooks/useLandingPage";
import { CartContext } from "../../../contexts/CartContext.jsx";
import AppContext from "../../../AppContext.jsx";
import { langComponent } from "../../../constants/index.jsx";

//style
import "../../../styles/allWorkExchangeModal.scss";
import "../../../styles/shoppingCart.scss";
import TrackingStatus from "../TrackingStatus";

import NotFoundIcon from "../../../assets/workExchange/no-data.png";
import PaymentForm from "../../Cart/PaymentForm.jsx";

function PromotePopup({
  onClose,
  work,
  setToggleSuccessModal,
  setDetailSuccess,
}) {
  const { createLandingPageConfig, getRanking, patchLandingPageConfig } =
    useLandingPage();
  const { queryJournals, createJournal, chargeJournal } = useJournals();
  const [togglePaymentModal, setTogglePaymentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ranking, setRanking] = useState([]);

  const { isDisabledPaymentBtn, setIsDisabledPaymentBtn } =
    useContext(CartContext);
  const { getLang } = useContext(AppContext);

  const Translation = getLang(langComponent.profile);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationPromote = getLang(langComponent.chat);
  const TranslationWorkExchange = getLang(langComponent.workExchange);

  const [creditCard, setCreditCard] = useState({
    cardNo1: "",
    cardNo2: "",
    cardNo3: "",
    cardNo4: "",
    name: "",
    expiryDate: "",
    CVC: "",
  });

  const [initialValues, setInitialValues] = useState({
    price: 0,
    effectiveDate: startOfDay(new Date()),
    dueDate: startOfDay(new Date()),
  });

  let charge = (initialValues.price * 3) / 100;

  let vat = (initialValues.price * 7) / 100;

  let grandTotal = initialValues.price + vat;

  const handleValidate = ({ price, effectiveDate, dueDate }) => {
    setInitialValues({
      price: price,
      effectiveDate: effectiveDate,
      dueDate: dueDate,
    });

    let errors = {};

    if (effectiveDate > dueDate) {
      errors.effectiveDate = "Can't come after ending date";
      errors.dueDate = "Can't come before starting date";
    }

    if (!price || !isValidNumber(price) || price == "") {
      errors.price = "Price can only be a positive number";
    }

    return errors;
  };

  const handleSeccess = async () => {
    onClose();
    setToggleSuccessModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleSubmit = async ({ price, effectiveDate, dueDate }) => {
    setTogglePaymentModal(true);

    if (togglePaymentModal == true) {
      let cardNo = `${creditCard.cardNo1}${creditCard.cardNo2}${creditCard.cardNo3}${creditCard.cardNo4}`;

      const [expirationMonth, expirationYear] =
        creditCard.expiryDate.split("/");

      const tokenParameters = {
        expiration_month: +expirationMonth,
        expiration_year: +expirationYear,
        number: cardNo,
        security_code: creditCard.CVC,
        name: creditCard.name,
      };

      await Omise.createToken(
        "card",
        tokenParameters,
        async (statusCode, response) => {
          if (!response || !response.card) {
            setIsSubmitting(false);
            console.error("Error calling omise");
            return;
          }
          setIsSubmitting(false);
          setIsDisabledPaymentBtn(true);

          const cardToken = response.id;

          //* create promote
          const promote = await createLandingPageConfig({
            section: 3,
            work: work._id,
            price: +initialValues.price,
            effectiveDate,
            dueDate,
            priorities: [],
            category: 1,
          });

          //* create journal
          const journal = await createJournal({
            details: [
              {
                work: promote.data._id,
                total: initialValues.price,
                discount: 0,
                subTotal: initialValues.price,
              },
            ],
            payments: [{ subTotal: grandTotal }],
            netTotal: grandTotal,
            vat: vat,
            serviceCharge: 0,
            grandTotal: grandTotal,
          });

          //* Charge Journal
          await chargeJournal(journal.data._id, {
            card: cardToken,
          });

          // * Patch Promote
          await patchLandingPageConfig(promote.data._id, {
            status: 2,
            journal: journal.data._id,
          });

          setDetailSuccess({
            transectionId: journal.data._id,
            cardNo: cardNo,
            price: grandTotal,
            createDate: journal.data.createdOn,
          });

          handleSeccess();
        }
      );
    }
  };

  const handleclose = () => {
    if (togglePaymentModal == true) {
      setTogglePaymentModal(false);
    } else {
      onClose();
    }
  };

  const convertTypeDateToISO = (dateTimeStr) => {
    // Thu Dec 07 2023 00:00:00 GMT+0700 (Indochina Time) => 2023-12-07T17:00:00.000Z
    try {
      const dateTime = new Date(dateTimeStr);

      const utcTime = new Date(
        dateTime.getTime() - dateTime.getTimezoneOffset() * 60000
      );

      utcTime.setUTCHours(17, 0, 0, 0);

      return utcTime.toISOString();
    } catch (err) {
      return "";
    }
  };

  useEffect(() => {
    if (
      Number(initialValues.price) == 0 ||
      initialValues.effectiveDate > initialValues.dueDate
    ) {
      setRanking([]);
    } else if (
      initialValues.price > 0 ||
      initialValues.effectiveDate < initialValues.dueDate
    ) {
      getRanking(
        initialValues.price ? initialValues.price : 0,
        convertTypeDateToISO(initialValues.effectiveDate),
        convertTypeDateToISO(initialValues.dueDate)
      ).then((_rank) => {
        setRanking(_rank);
      });
    }
  }, [initialValues]);

  return (
    <Overlay
      onClose={onClose}
      style={{
        alignItems: "flex-start",
      }}
    >
      <Formik
        initialValues={{
          effectiveDate: startOfDay(new Date()),
          dueDate: startOfDay(new Date()),
          price: undefined,
        }}
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        <Form>
          <div className="promote-container">
            <div className="promote-container-content">
              <div className="promote-container-content-promote">
                <div className="promote-container-content-promote-header">
                  {TranslationCommon("Promote")}
                </div>
                {togglePaymentModal ? (
                  <PaymentForm creditCard={creditCard} />
                ) : (
                  <div className={`promote-container-content-promote-detail `}>
                    <div
                      className={`promote-container-content-promote-detail-info `}
                    >
                      <div className="promote-container-content-promote-detail-info-banner">
                        <div className="promote-container-content-promote-detail-info-banner__image">
                          <img src={work.banner} />
                        </div>
                        <div className="promote-container-content-promote-detail-info-banner__name">
                          {work.name}
                        </div>
                      </div>
                      <div className="promote-container-content-promote-detail-info-form">
                        <div className="promote-container-content-promote-detail-info-form-header">
                          {TranslationWorkExchange("Input your promote price")}
                        </div>
                        <div className="promote-container-content-promote-detail-info-form-input">
                          <div className="promote-container-content-promote-detail-info-form-input-textbox">
                            <TextBox
                              label={TranslationPromote("Price")}
                              name="price"
                              placeholder={TranslationPromote("Price")}
                              type="number"
                              autoComplete={false}
                            />
                          </div>
                          <div className="promote-container-content-promote-detail-info-form-input-date">
                            <div className="promote-container-content-promote-detail-info-form-input-date__wrp">
                              <DatePicker
                                name="effectiveDate"
                                label={TranslationCommon("Start Date")}
                                dateFormat="dd/MM/yyyy"
                                placeholder={TranslationCommon("Start Date")}
                              />
                              <CalendarSvg className="promote-container-content-promote-detail-info-form-input-date__wrp--icon" />
                            </div>
                            <div className="promote-container-content-promote-detail-info-form-input-date__wrp">
                              <DatePicker
                                name="dueDate"
                                label={TranslationCommon("End Date")}
                                dateFormat="dd/MM/yyyy"
                                placeholder={TranslationCommon("End Date")}
                              />
                              <CalendarSvg className="promote-container-content-promote-detail-info-form-input-date__wrp--icon" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {ranking.length > 0 ? (
                      <div className="promote-container-content-promote-detail-tracking">
                        <div className="promote-container-content-promote-detail-tracking-wrp">
                          {ranking.map((rank, index) => {
                            return (
                              <TrackingStatus tracking={rank} key={index} />
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="promote-container-content-promote-detail-tracking__no-data">
                        <img src={NotFoundIcon} />
                        <div className="promote-container-content-promote-detail-tracking__no-data--text">
                          {TranslationWorkExchange(
                            "Please fill out all information."
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="promote-container-content-summary">
                <div className="promote-container-content-summary-header">
                  {Translation("Summary")}
                </div>
                <div className="promote-container-content-summary-detail">
                  <div className="promote-container-content-summary-detail-content">
                    <div className="promote-container-content-summary-detail-content__price">
                      <div>{TranslationPromote("Price")}</div>
                      <div>
                        {initialValues.price
                          ? initialValues.price.toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div className="promote-container-content-summary-detail-content__charge">
                      <div>{TranslationPromote("Vat 7%")}</div>
                      <div>{vat ? vat.toLocaleString() : 0}</div>
                    </div>
                    <div className="promote-container-content-summary-detail-content__total">
                      <div>{TranslationPromote("Grand Total")}</div>
                      <div>{grandTotal ? grandTotal.toLocaleString() : 0}</div>
                    </div>
                  </div>
                  <div className="promote-container-content-summary-detail-footer">
                    <Button
                      variant={"secondary"}
                      label={TranslationCommon("Cancel")}
                      onClick={handleclose}
                    />
                    <Button
                      label={TranslationCommon("Confirm")}
                      type="submit"
                      submitting={togglePaymentModal ? isSubmitting : false}
                      disabled={
                        togglePaymentModal
                          ? isDisabledPaymentBtn == true
                          : initialValues.price > 0
                          ? false
                          : true
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Formik>
    </Overlay>
  );
}

PromotePopup.propTypes = {};

export default PromotePopup;
